/*
  <ia-pdp-loyalty> — the rewards row's number (blocks/_ia-pdp-loyalty.liquid).

  Points follow the price the page shows: the variant, the selling plan once
  one is chosen, or a bundle builder's total, times the quantity. The maths
  is Yotpo's purchase rule — whole dollars × points per dollar, × the
  shopper's tier multiplier, floored — so every change is local arithmetic;
  nothing is fetched per interaction.

  The rate and the multiplier arrive from Liquid (data-rate, data-multiplier,
  data-tier). With data-verify the live values are read once per session from
  Yotpo's public loyalty endpoints (the purchase campaign and the tier table,
  keyed by data-guid) and cached in sessionStorage; a failed read keeps the
  Liquid values, so the row never depends on Yotpo answering.

  Inputs (ia-util-pubsub), all scoped to this block's section:
  - variant:change      the variant's price
  - plan/cadence:change the plan's price (plan_price_cents) or back to the variant
  - bundle:change       the builder's discounted total (the parent price is
                        never shown for a bundle: data-bundle waits for it)
  - qty:change          the buy form's quantity input (name "quantity")
*/
import { EVENTS, subscribe } from 'ia-util-pubsub'
import { debugEnabled } from 'ia-util-helpers'

const DEBUG = debugEnabled()
const CAMPAIGNS_URL = 'https://cdn-loyalty.yotpo.com/api/public/v1/campaigns'
const TIERS_URL = 'https://loyalty.yotpo.com/api/public/v1/tiers'
const PURCHASE_CAMPAIGN = 'PointsForPurchasesCampaign'
const CACHE_TTL_MS = 60 * 60 * 1000
const MAIN_QTY_NAME = 'quantity'

function debug(label, data) {
  if (DEBUG) console.log(`[ia-pdp-loyalty] ${label}`, data ?? '')
}

class IaPdpLoyalty extends HTMLElement {
  connectedCallback() {
    this.pointsEl = this.querySelector('[data-points]')
    this.badgeEl = this.querySelector('[data-points-badge]')
    this.sectionId = this.dataset.sectionId
    this.section = this.closest('.shopify-section')

    this.rate = parseFloat(this.dataset.rate) || 1
    this.multiplier = parseFloat(this.dataset.multiplier) || 1
    this.variantPrice = parseInt(this.dataset.price, 10) || 0
    this.planPrice = null
    this.bundleTotal = this.readBundleTotal()
    this.quantity = 1

    this.unsubscribers = [
      subscribe(EVENTS.variantChange, this.handleVariantChange.bind(this)),
      subscribe(EVENTS.planChange, this.handlePlanChange.bind(this)),
      subscribe(EVENTS.cadenceChange, this.handlePlanChange.bind(this)),
      subscribe(EVENTS.bundleChange, this.handleBundleChange.bind(this)),
      subscribe(EVENTS.qtyChange, this.handleQtyChange.bind(this)),
    ]

    this.render()
    if (this.hasAttribute('data-verify')) this.verifyRates()
  }

  disconnectedCallback() {
    this.unsubscribers?.forEach((unsubscribe) => unsubscribe())
  }

  // A builder that ran before this element connected left its total on itself.
  readBundleTotal() {
    const builder = this.section?.querySelector('ia-custom-bundle, ia-routine-bundle')
    if (!builder?.dataset.total) return null
    return parseInt(builder.dataset.total, 10) || 0
  }

  get price() {
    if (this.bundleTotal !== null) return this.bundleTotal
    if (this.planPrice !== null) return this.planPrice
    return this.variantPrice
  }

  // Yotpo's rule: whole dollars earn, the tier multiplies, both floored.
  get points() {
    const base = Math.floor((this.price / 100) * this.rate)
    return Math.floor(base * this.multiplier) * this.quantity
  }

  inSection(detail) {
    return !detail?.sectionId || detail.sectionId === this.sectionId
  }

  handleVariantChange({ detail }) {
    if (!this.inSection(detail) || !detail?.variant) return
    this.variantPrice = detail.variant.price
    // The subscription block re-renders and announces its plan price again.
    this.planPrice = null
    this.render()
  }

  handlePlanChange({ detail }) {
    if (!this.inSection(detail)) return
    const onPlan = detail?.cadence && detail.cadence !== 'one-time' && detail.plan
    const cents = parseInt(detail?.plan_price_cents, 10)
    this.planPrice = onPlan && !Number.isNaN(cents) ? cents : null
    this.render()
  }

  handleBundleChange({ detail }) {
    const owner = detail?.blockId && document.getElementById(`shopify-block-${detail.blockId}`)
    if (!owner || !this.section?.contains(owner)) return
    this.bundleTotal = detail.total
    this.render()
  }

  handleQtyChange({ detail }) {
    if (detail?.name !== MAIN_QTY_NAME) return
    const qty = parseInt(detail.value, 10)
    this.quantity = qty > 0 ? qty : 1
    this.render()
  }

  render() {
    const points = this.points
    if (this.pointsEl) this.pointsEl.textContent = points
    if (this.badgeEl) this.badgeEl.hidden = points <= 0
    debug('render', { price: this.price, rate: this.rate, multiplier: this.multiplier, quantity: this.quantity, points })
  }

  // The live rule and tier table, once per session per store.
  async verifyRates() {
    const guid = this.dataset.guid
    if (!guid) return
    try {
      const rates = await this.loadRates(guid)
      if (rates.rate > 0) this.rate = rates.rate
      const tier = rates.tiers.find((t) => t.name === (this.dataset.tier || '').trim().toLowerCase())
      if (tier && tier.multiplier > 0) this.multiplier = tier.multiplier
      debug('verified', { rate: this.rate, multiplier: this.multiplier, tier: this.dataset.tier })
      this.render()
    } catch (error) {
      debug('verify failed, keeping the settings', error.message)
    }
  }

  async loadRates(guid) {
    const key = `ia-loyalty-rates:${guid}`
    const cached = this.readCache(key)
    if (cached) return cached

    const query = `?guid=${encodeURIComponent(guid)}`
    const [campaigns, tiers] = await Promise.all([
      fetch(CAMPAIGNS_URL + query).then((r) => r.json()),
      fetch(TIERS_URL + query).then((r) => r.json()),
    ])
    const purchase = campaigns.find((c) => c.type === PURCHASE_CAMPAIGN && c.active)
    const rates = {
      rate: purchase ? (purchase.reward_points * 100) / (purchase.goal_spend_cents || 100) : 0,
      tiers: tiers.map((t) => ({ name: t.name.trim().toLowerCase(), multiplier: parseFloat(t.points_multiplier) })),
    }
    this.writeCache(key, rates)
    return rates
  }

  readCache(key) {
    try {
      const entry = JSON.parse(sessionStorage.getItem(key))
      if (entry && Date.now() - entry.at < CACHE_TTL_MS) return entry.rates
    } catch {
      // No storage, or nothing usable in it: fetch instead.
    }
    return null
  }

  writeCache(key, rates) {
    try {
      sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), rates }))
    } catch {
      // Storage unavailable: the fetch simply repeats next time.
    }
  }
}

if (!customElements.get('ia-pdp-loyalty')) {
  customElements.define('ia-pdp-loyalty', IaPdpLoyalty)
}
